import { API_DATE_FORMAT, DATE_FORMAT } from "@/app/utils/constants/constants";
import { MAX_STAY_DURATION_DAYS } from "@/constants/stay";
import type { FlightSearchRequest } from "@/types/flights";
import { Calendar } from "antd";
import { Dayjs } from "dayjs";
import dayjs from "@dayjs";
import { useTheme } from "next-themes";
import React, {
  Fragment,
  useEffect,
  useRef,
  useState,
  type MouseEventHandler,
} from "react";
import CalendarModal from "../../DatePicker/CalendarModal";
import { CalendarsIcon, Cross } from "../../Icons";
import { useLocale } from "next-intl";
import { useTranslations } from "use-intl";
import { getWeekdays } from "@/app/utils/date/getWeekdays";

type CalendarHeaderProps = {
  date: Dayjs;
};

const CalendarHeader = ({ date }: CalendarHeaderProps) => (
  <div className="text-center text-lg font-bold shadow dark:bg-sleap-gray-850 bg-lightwhite">
    {date.format("MMMM YYYY")}
  </div>
);

type MonthCalendarProps = {
  monthOffset: number;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  date: [Dayjs | null, Dayjs | null];
  setDate: React.Dispatch<React.SetStateAction<[Dayjs | null, Dayjs | null]>>;
  departOnly?: boolean;
};

const MonthCalendar = ({
  monthOffset,
  date,
  setDate,
  departOnly,
}: MonthCalendarProps) => {
  const month = dayjs().add(monthOffset, "month");

  const isDateSelectable = (currentDate: Dayjs) => {
    const startOfToday = dayjs().startOf("day");
    const isDateBeforeToday = currentDate.isBefore(startOfToday, "day");

    // disallow selection of past dates.
    if (isDateBeforeToday) {
      return false;
    }

    const isStartDateSelected = Boolean(date[0]);
    const isEndDateSelected = Boolean(date[1]);

    // if a start date is already selected but not an end date, handle he range.
    if (isStartDateSelected && !isEndDateSelected) {
      const startToDateDiff = currentDate.diff(date[0], "day");

      // allow selection only if the curr date is after the start date and 30 days range
      const isAfterStartDate = startToDateDiff > 0;
      const isWithin30DaysRange = startToDateDiff <= MAX_STAY_DURATION_DAYS;

      return isAfterStartDate && isWithin30DaysRange;
    }

    // if both start and end dates are selected, or no start date is not selected allow any date to be selected
    return !isStartDateSelected || (isStartDateSelected && isEndDateSelected);
  };

  const disabledDate = (currentDate: Dayjs) => {
    const today = dayjs().startOf("day");
    return currentDate.isBefore(today, "day"); // Disable dates before today
  };

  const fullCellRender = (current: Dayjs) => {
    const isSelectedStart =
      date[0] &&
      current.isSame(date[0], "day") &&
      current.isSame(date[0], "month") &&
      current.isSame(date[0], "year");
    const isSelectedEnd =
      date[1] &&
      current.isSame(date[1], "day") &&
      current.isSame(date[1], "month") &&
      current.isSame(date[1], "year");
    const isInRange =
      date[0] &&
      date[1] &&
      current.isAfter(date[0], "day") &&
      current.isBefore(date[1], "day");
    const isCurrentMonth = current.month() === month.month();

    let cellClass = "";

    if (isSelectedStart) {
      cellClass = "start-date";
    } else if (isSelectedEnd) {
      cellClass = "end-date";
    } else if (isInRange) {
      cellClass = "range-date";
    }

    return (
      <div
        className={`ant-picker-cell-inner ${cellClass} ${!isCurrentMonth && "ant-picker-cell-not-in-view"} !min-w-10 h-14 pt-2 flex justify-center items-center}`}
      >
        {current.date()}
      </div>
    );
  };

  const onSelect = (_date: Dayjs) => {
    if (departOnly) {
      setDate([dayjs(_date, DATE_FORMAT), null]);
    } else {
      if (!date[0] || (date[0] && date[1])) {
        setDate([dayjs(_date, DATE_FORMAT), null]);
      } else {
        const fromDate: Dayjs = date[0]!;
        setDate([fromDate, dayjs(_date, DATE_FORMAT)]);
      }
    }
  };

  return (
    <div>
      <CalendarHeader date={month} />
      <Calendar
        fullscreen={false}
        headerRender={() => null}
        value={month}
        disabledDate={(current) => !isDateSelectable(current)}
        onSelect={onSelect}
        fullCellRender={fullCellRender}
      />
    </div>
  );
};

type Props = {
  departOnly?: boolean;
  departDate: string;
  returnDate: string;
  updateFlightField: (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => void;
  flightId: string;
};

const MobileCalendars = ({
  departOnly,
  departDate,
  returnDate,
  updateFlightField,
  flightId,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations();
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [date, setDate] = useState<[Dayjs | null, Dayjs | null]>([
    departDate ? dayjs(departDate, API_DATE_FORMAT) : null,
    returnDate ? dayjs(returnDate, API_DATE_FORMAT) : null,
  ]);

  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (departOnly) {
      setDate([departDate ? dayjs(departDate, API_DATE_FORMAT) : null, null]);
    } else {
      setDate([
        departDate ? dayjs(departDate, API_DATE_FORMAT) : null,
        returnDate ? dayjs(returnDate, API_DATE_FORMAT) : null,
      ]);
    }
  }, [departDate, returnDate]);

  const toggleVisibility = () => {
    if (isVisible) {
      setScrollPosition(containerRef.current?.scrollTop ?? 0);
    } else {
      if (containerRef.current) {
        containerRef.current.scrollTop = scrollPosition;
      }
    }
    setIsVisible(!isVisible);
  };

  const formatDisplayDate = (date: Dayjs | null) => {
    if (!date) return "";

    return dayjs(date)
      .locale(locale) // 👈 apply locale here
      .format(locale === "en" ? "ddd, MMM D" : "YYYY-MM-DD");
  };

  const weekdays = getWeekdays(t);

  useEffect(() => {
    if (isVisible && containerRef.current) {
      containerRef.current.scrollTop = scrollPosition;
    }
  }, [isVisible, scrollPosition]);

  const handleConfirm: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (date[0]) {
      const formattedDepartDate = date[0].format(API_DATE_FORMAT);
      const formattedReturnDate = date[1]?.format(API_DATE_FORMAT);

      updateFlightField(flightId, {
        departDate: formattedDepartDate,
        ...(formattedReturnDate && { returnDate: formattedReturnDate }),
      });
    }

    if (departOnly) {
    }

    setIsVisible(departOnly ? !date[0] : !(date[0] && date[1]));
  };

  const handleCancel: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    setDate([
      departDate ? dayjs(departDate, API_DATE_FORMAT) : null,
      returnDate ? dayjs(returnDate, API_DATE_FORMAT) : null,
    ]);
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isVisible]);

  return (
    <>
      <button
        type="button"
        onClick={toggleVisibility}
        className="mobile-date-picker w-full flex flex-row items-center rounded-2xl gap-4 !px-4 border-[1px] 2xl:border-2 dark:border-input border-sleap-gray-400 dark:bg-sleap-gray-950 bg-lightwhite dark:hover:border-white hover:border-sleap-gray-600 text-sleap-gray-950 dark:text-lightwhite h-14"
      >
        {date?.[0] ? (
          <div className="flex w-full items-center date-picker-container">
            <span className="-ms-0.5">
              <CalendarsIcon
                color={resolvedTheme === "dark" ? "offWhite" : "lightblack"}
              />
            </span>
            <div className="flex justify-center items-center w-full mx-auto selected-dates-string">
              <p className="mb-0 text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                {formatDisplayDate(date[0])}
              </p>
              {!departOnly && (
                <Fragment>
                  <span className="ant-picker-range-separator-mobile">-</span>
                  <p className="mb-0 text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                    {formatDisplayDate(date[1])}
                  </p>
                </Fragment>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center gap-x-3 w-full mx-auto ">
            <CalendarsIcon
              color={resolvedTheme === "dark" ? "offWhite" : "lightblack"}
            />

            <div className="flex justify-center">
              <p className="mb-0 text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                {t("Other.startDate")}
              </p>
              {!departOnly && (
                <>
                  <span className="ant-picker-range-separator-mobile">-</span>
                  <p className="mb-0 text-base font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                    {t("Other.endDate")}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </button>
      <CalendarModal
        isOpen={isVisible}
        width="w-full pt-8 bg-lightwhite dark:bg-sleap-gray-950"
        bgColor="bg-lightwhite dark:bg-sleap-gray-950"
      >
        <div
          ref={containerRef}
          className={`mobile-date-picker-container !bg-lightwhite dark:!bg-sleap-gray-950 ${!isVisible ? "hidden" : ""}`}
        >
          <div className="flex justify-between items-center p-4">
            <h4 className="text-sleap-gray-950 dark:text-lightwhite font-bold font-unbounded text-lg mb-0 ">
              {t("Other.calendar")}
            </h4>
            <span
              className="p-2 rounded-full bg-sleap-gray-200 dark:bg-sleap-gray-600 cursor-pointer"
              onClick={handleCancel}
            >
              <Cross color={resolvedTheme === "dark" ? "#fff" : "#09090B"} />
            </span>{" "}
          </div>
          <div className=" dark:bg-sleap-gray-850 bg-lightwhite text-sleap-gray-800 dark:text-lightwhite mx-auto z-50 w-full top-[72px]">
            <div className="flex items-center justify-center gap-2">
              {weekdays.map((day, index) => (
                <p key={index} className="mb-0 p-3 text-base font-normal">
                  {day}
                </p>
              ))}
            </div>
          </div>
          <div className="bg-lightwhite dark:bg-sleap-gray-950">
            {Array.from({ length: 12 }).map((_, index) => (
              <MonthCalendar
                key={index}
                monthOffset={index}
                setIsVisible={setIsVisible}
                date={date}
                setDate={setDate}
                departOnly={departOnly}
              />
            ))}
          </div>

          <div className="dark:border border-sleap-gray-850 rounded-b-xl dark:bg-sleap-gray-850 bg-white shadow-confirm-btn px-4 fixed bottom-0  py-4 z-50 w-full">
            <div className="flex items-center justify-between w-full">
              <button
                className="pointer flex items-center primary-border px-3 py-2 rounded-xl gap-x-2 w-full"
                onClick={handleConfirm}
              >
                <h5 className="text-base font-semibold font-inter text-white mb-0">
                  {t("Other.confirm")}
                </h5>
              </button>
            </div>
          </div>
        </div>
      </CalendarModal>
    </>
  );
};

export default MobileCalendars;
